import { NamedRoute } from "@/jenesys-types/routes/routes";

import { RootRoute } from "@/components/routes/RootRoute";

import AppRoutes from "./AppRoutes";
import { setupClientRoutes, setupPracticeRoutes } from "./company/company";
import { lazyWithRetry } from "./lazyWithRetry";
import { RootIndexRoute } from "./RootIndexRoute";

// Lazy load major route components
const ClientRoute = lazyWithRetry(() =>
  import("@/components/routes/clients/ClientRoute").then((m) => ({
    default: m.ClientRoute
  }))
);
const CompanyRoute = lazyWithRetry(() =>
  import("@/components/routes/company/CompanyRoute").then((m) => ({
    default: m.CompanyRoute
  }))
);
const OperatorPage = lazyWithRetry(() =>
  import("@/components/features/operator/OperatorPage").then((m) => ({
    default: m.OperatorPage
  }))
);

/**
 * Creates application routes with two completely separate patterns:
 * 1. /client/:clientId/* - For client entities ONLY
 * 2. /company/:companyId/* - For practice entities ONLY
 *
 * This ensures clients and companies have distinct route hierarchies
 * with absolutely no nesting between routes.
 */
const setupRoutes = (): Array<NamedRoute> => {
  // Setup practice routes (for company entities only)
  const practiceRoutes = setupPracticeRoutes();

  // Setup distinct routes for clients
  const clientRoutes = setupClientRoutes();

  return [
    {
      name: "root",
      path: "/",
      element: <RootRoute />,
      children: [
        {
          index: true,
          element: <RootIndexRoute />
        },
        // Client route pattern - completely separate from company routes
        {
          name: "client",
          path: "client/:clientId",
          element: <ClientRoute />,
          children: [
            {
              element: <AppRoutes />,
              children: clientRoutes
            }
          ]
        },
        // Company route pattern - completely separate from client routes
        {
          name: "company",
          path: "company/:companyId",
          element: <CompanyRoute />,
          children: [
            {
              element: <AppRoutes />,
              children: practiceRoutes
            }
          ]
        },
        // Operator route - hidden route for operator user only
        {
          name: "operator",
          path: "operator",
          element: <OperatorPage />
        }
      ]
    }
  ];
};

export default setupRoutes;
